import axios from "axios";
import { currentPipeline, setCurrentPipeline, LIVE, PL_HEADER } from "@/lib/pipeline";

const RAW_BACKEND = process.env.REACT_APP_BACKEND_URL;
// Bulletproof — force HTTPS in production. In localhost dev keep http.
const BACKEND_URL = (() => {
  if (!RAW_BACKEND) return "";
  if (typeof window !== "undefined" && window.location.protocol === "https:" && RAW_BACKEND.startsWith("http://") && !RAW_BACKEND.includes("localhost")) {
    return RAW_BACKEND.replace("http://", "https://");
  }
  // Strip any trailing slash so `${API}/foo` never becomes `//foo`.
  return RAW_BACKEND.replace(/\/+$/, "");
})();
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  // Never throw on the raw JSON parser — a proxy 502/504 sometimes returns an
  // HTML error page instead of JSON. We swallow the parse error here and let
  // the calling code handle a null/undefined body instead of a red console
  // stack trace that would look terrible during a live client pitch.
  transformResponse: [(data, headers) => {
    if (data == null || data === "") return data;
    const ct = (headers && (headers["content-type"] || headers["Content-Type"])) || "";
    if (ct.includes("application/json")) {
      try { return JSON.parse(data); } catch (_) { return { __parse_error: true, raw: String(data).slice(0, 500) }; }
    }
    if (typeof data === "string" && data.trim().startsWith("{")) {
      try { return JSON.parse(data); } catch (_) { return { __parse_error: true, raw: data.slice(0, 500) }; }
    }
    return data;
  }],
});

// Global 401 handling — surface eviction and expiry messages, then send the
// user back to the login page so they can re-authenticate.
const GENERIC_401 = new Set([
  "Not authenticated",
  "Session invalid or signed out",
  "Invalid session",
  "Session expired",
  "User not found",
]);
// v47 — every My Radar call works in the open pipeline unless it says otherwise.
api.interceptors.request.use((config) => {
  const url = config.url || "";
  if (url.startsWith("/my-radar") && !(config.headers && config.headers[PL_HEADER])) {
    config.headers = config.headers || {};
    config.headers[PL_HEADER] = currentPipeline();
  }
  return config;
});
let _plResetPending = false;
api.interceptors.response.use((r) => r, (err) => {
  if (err?.response?.status === 404 && err?.response?.data?.detail === "pipeline not found" && !_plResetPending) {
    _plResetPending = true;
    setCurrentPipeline(LIVE);
    if (window.location.pathname.startsWith("/my-radar")) window.location.replace("/my-radar?pl=live");
  }
  return Promise.reject(err);
});

let _authRedirectPending = false;
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      const detail = err?.response?.data?.detail || "";
      const url = err?.config?.url || "";
      const isMeCheck = url.includes("/auth/me");
      // Stash only ACTIONABLE detail (e.g. eviction / revoked / expired).
      // Generic "not authenticated" strings are noise for anonymous visitors.
      if (detail && !GENERIC_401.has(detail)) {
        try { sessionStorage.setItem("signin_error", detail); } catch (_) {}
      }
      // For the initial `/auth/me` probe OR any generic "Not authenticated"
      // response (anonymous probes from public pages like Landing) stay silent
      // — the router already handles unauthenticated users where needed.
      if (!isMeCheck && !GENERIC_401.has(detail) && !_authRedirectPending) {
        _authRedirectPending = true;
        setTimeout(() => {
          _authRedirectPending = false;
          if (window.location.pathname !== "/login") {
            window.location.assign("/login");
          }
        }, 250);
      }
    }
    return Promise.reject(err);
  }
);

export const fmtNumber = (n) => {
  if (n === null || n === undefined) return "—";
  const num = Number(n);
  if (isNaN(num)) return "—";
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(num >= 10_000_000 ? 0 : 1) + "M";
  if (num >= 1_000) return (num / 1_000).toFixed(num >= 10_000 ? 0 : 1) + "K";
  return num.toLocaleString();
};

export const fmtPct = (v) => {
  if (v === null || v === undefined) return "—";
  return `${Number(v).toFixed(2)}%`;
};
